import { useEffect, useState, type JSX } from 'react';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { FormInstance } from 'antd';
import { Dayjs } from 'dayjs';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { languageOptions, randomId } from '@app/constants';
import { paths } from '@app/constants/paths.constant';
import { changeLanguage, getCurrentAppTheme, useDetectLanguage } from '@app/utils';
import { Language, TokenData } from '@data';
import { HospitalCenterCreate, Gender, Role, TokenType } from '@data/sdk/match';
import { useUserFakeIdStore } from '@data/storage';
import { useTrackEvents } from '@data/utils/events';
import {
  AntdConfigProvider,
  AntdForm,
  AntdSelect,
  AntdSpace,
  AntdTypography,
  Button,
  Card,
  isYWC,
  useTheme,
  YWCLogoSmall,
} from '@ui';
import { DiseaseForm } from './DiseasesForm';
import { useHandleSignUp } from './hooks';
import UserInfoForm from './UserInfoForm';

import './sign-up.css';

type FormStepType<T> = {
  form: (props: T) => JSX.Element;
  props: T;
};

export type FormStepProps = {
  form: FormInstance<SignUpFormValues>;
};

export type UserInfoFormProps = {
  addHospital: boolean;
  anonymousSignUp?: boolean;
  form: FormInstance<SignUpFormValues>;
  handleBack?: () => void;
  initialValues?: TokenData;
  isVolunteer?: boolean;
  setAddHospital: (value: boolean) => void;
};

export interface CustomHospitalValues extends Omit<HospitalCenterCreate, 'language' | 'country'> {
  country: { label: string; value: string };
}

export interface SignUpFormValues {
  behalfOf: { label: string; value: string };
  birthDate: Dayjs;
  confirmPassword: string;
  country?: { label: string; value: string };
  customHospital?: CustomHospitalValues;
  diseases?: { label: string; value: string }[];
  email: string;
  firstName: string;
  gender?: Gender;
  haveDisease: boolean;
  hospital: { label: string; value: string };
  lastName: string;
  password: string;
  phoneNumber?: string;
  privacyPolicy: boolean;
  termsOfUse: boolean;
  zipCode?: string;
}

const { Text, Title } = AntdTypography;

const SignUpPage = () => {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const { baseSelect, textColorDark } = useTheme();
  const { lang, changeQueryLanguage } = useDetectLanguage(i18n);

  const [formStep, setFormStep] = useState(0);
  const [form] = AntdForm.useForm<SignUpFormValues>();
  const [addHospital, setAddHospital] = useState(false);

  const { events, trackEvent } = useTrackEvents();
  const { tokenData, handleSignUp } = useHandleSignUp(form);
  const { user, setUser, removeUser } = useUserFakeIdStore();
  const { PublicLogo, publicLogoProps, AdditionalLogo, additionalLogoProps } = getCurrentAppTheme();

  useEffect(() => {
    // skip diseases step for anonymously created users
    if (tokenData?.tokenType === TokenType.AccountClaim) {
      setFormStep(1);
    }
  }, [tokenData]);

  const handleBack = () => setFormStep(formStep - 1);

  const FormSteps = [
    { form: DiseaseForm, props: { form } } as FormStepType<FormStepProps>,
    {
      form: UserInfoForm,
      props: {
        form,
        handleBack,
        initialValues: tokenData,
        addHospital,
        setAddHospital,
        anonymousSignUp: tokenData?.tokenType === TokenType.AccountClaim,
      },
    } as FormStepType<UserInfoFormProps>,
  ];

  const FormStep = FormSteps[formStep].form;
  const FormProps = FormSteps[formStep].props;

  useEffect(() => {
    if (!user && formStep === 0) {
      setUser(randomId);
      trackEvent(events.SignUpInitial, { role: Role.Volunteer, userId: randomId });
    }
    return () => {
      if (user) removeUser();
    };
  }, []);

  return (
    <AntdSpace orientation="vertical" className="sm:max-w-md md:pt-6">
      <div className="hidden md:block pl-6 sm:pl-0 pt-16 sm:pt-0 rounded-bl-3xl rounded-br-3xl">
        <Title className="text-base-100 mb-0 mt-0 sm:mt-auto">{t('sign_up_page.title')}</Title>

        <Text className="text-base-100">
          <Trans
            t={t}
            i18nKey="set_password_page.already_have_an_account"
            components={{
              b: (
                <Button
                  className="inline-flex p-0 text-base-100 underline"
                  type="link"
                  onClick={() => navigate(`/${paths.sign_in}`)}
                />
              ),
            }}
          />
        </Text>

        {isYWC && <PublicLogo {...publicLogoProps} />}
      </div>

      <Card className="border-none sm:border-solid sm:max-w-md lg:mt-9">
        <div className="flex w-full self-start justify-between mb-4">
          {isYWC ? (
            <YWCLogoSmall className="h-20 w-auto md:hidden" />
          ) : (
            <PublicLogo {...publicLogoProps} />
          )}
          <div className="flex items-center gap-2 ml-auto">
            <a
              href={t('faq.link')}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 md:hidden"
              style={{ color: textColorDark }}
            >
              {t('faq.text_short')}
              <QuestionCircleOutlined />
            </a>
            <AntdConfigProvider theme={{ components: { Select: baseSelect } }}>
              <AntdSelect
                variant="borderless"
                defaultValue={lang ?? i18n.language}
                onChange={(value) => {
                  changeLanguage(value, i18n);
                  changeQueryLanguage(value as Language);
                }}
                aria-label="Language"
                options={languageOptions()}
              />
            </AntdConfigProvider>
          </div>
        </div>

        <AntdForm.Provider
          onFormFinish={(name) => {
            if (name === 'disease-form') {
              setFormStep(formStep + 1);
              trackEvent(events.SignUpNextStep, {
                role: Role.Volunteer,
                userId: user,
              });
            }
            if (name === 'user-info-form') {
              handleSignUp(form.getFieldsValue(true), addHospital);
            }
          }}
        >
          <FormStep {...FormProps} addHospital={addHospital} setAddHospital={setAddHospital} />
        </AntdForm.Provider>

        <div className="mt-4 sm:hidden">
          <Trans
            t={t}
            i18nKey="set_password_page.already_have_an_account"
            components={{
              b: (
                <Button
                  className="inline-flex p-0 underline"
                  type="link"
                  onClick={() => navigate(`/${paths.sign_in}`)}
                />
              ),
            }}
          />
        </div>

        <AdditionalLogo {...additionalLogoProps} />
      </Card>
    </AntdSpace>
  );
};

export default SignUpPage;
